import { useState, useRef, useContext } from 'react';
import {
  Link,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { AuthContext } from '@/features/auth/context/AuthContext';
import { getRolePanelPath } from '@/features/auth/roleRoutes';
import { normalizeIranPhone, isValidIranPhone } from '@/lib/phone';
import { getPersianErrorMessage } from '@/lib/persianUi';
import TurnstileWidget from './TurnstileWidget';
import '../auth.css';

const translateAuthError = (error) => {
  const message = error?.message || '';

  const errorMessages = {
    'Wrong password': 'رمز عبور اشتباه است.',
    'Invalid password': 'رمز عبور اشتباه است.',
    'Incorrect phone number or password': 'شماره موبایل یا رمز عبور اشتباه است.',
    'Invalid credentials': 'شماره موبایل یا رمز عبور اشتباه است.',
    'User not found': 'کاربری با این شماره موبایل پیدا نشد.',
    'Inactive user':
      'حساب شما هنوز فعال نشده است. لطفاً پس از تأیید مدیر دوباره تلاش کنید.',
    'User already exists': 'این شماره موبایل قبلاً ثبت شده است.',
    'Invalid phone number': 'شماره موبایل وارد شده معتبر نیست.',
    'Account disabled': 'حساب کاربری شما غیرفعال شده است.',
    'Too many requests':
      'تعداد تلاش‌ها بیش از حد مجاز است. لطفاً کمی بعد دوباره تلاش کنید.',
    'Too many failed login attempts':
      'تعداد تلاش‌های ورود بیش از حد مجاز بود. لطفاً ۱۵ دقیقه دیگر دوباره تلاش کنید.',
    'captcha_required': 'برای امنیت بیشتر، لطفاً تایید امنیت را تکمیل کنید.',
    'captcha_failed': 'تایید امنیت ناموفق بود. لطفاً دوباره تلاش کنید.',
    'captcha_unavailable': 'سرویس تایید امنیت در دسترس نیست. لطفاً چند لحظه بعد دوباره تلاش کنید.',
  };

  return (
    errorMessages[message] ||
    'ورود به حساب کاربری ناموفق بود. لطفاً اطلاعات وارد شده را بررسی کنید.'
  );
};


// -------------------------------------------------------------------------

export default function Login() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [touched, setTouched] = useState({ phone: false, password: false });

  // Turnstile state: only show widget after backend returns 428
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);

  const successMessage = location.state?.message;

  const phoneValid = isValidIranPhone(phoneNumber);
  const passwordValid = password.length > 0;

  const phoneError =
    touched.phone && !phoneValid
      ? 'شماره موبایل باید ۱۰ رقم و بدون صفر باشد (مثلا ۹۱۲۳۴۵۶۷۸۹)'
      : '';
  const passwordError =
    touched.password && !passwordValid ? 'رمز عبور را وارد کنید' : '';

  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

  const handlePhoneChange = (event) => {
    setPhoneNumber(normalizeIranPhone(event.target.value));
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    setTouched({ phone: true, password: true });

    if (!phoneValid || !passwordValid) {
      return; // don't even hit the backend if the shape is wrong
    }

    setError('');
    setLoading(true);

    try {
      const loginResult = await login(
        phoneNumber, // already the clean 10-digit number
        password,
        captchaToken || null
      );

      if (loginResult.requiresRoleSelection) {
        navigate('/select-role', { replace: true });
        return;
      }

      const role = loginResult.role;

      const fromLocation = location.state?.from;
      const from = fromLocation
        ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
        : null;

      const rolePanelPath =
        getRolePanelPath(role) || loginResult.redirectTo || '/';
      navigate(role === 'customer' ? from || rolePanelPath : rolePanelPath);
    } catch (err) {
      // 428 Precondition Required: show captcha widget
      if (err.code === 'captcha_required') {
        if (!turnstileSiteKey) {
          // Backend requires captcha but frontend has no site key configured
          setError('پیکربندی تایید امنیت ناقص است. لطفاً با پشتیبانی تماس بگیرید.');
        } else {
          setShowCaptcha(true);
          setCaptchaToken('');
          if (turnstileRef.current) {
            turnstileRef.current.reset();
          }
        }
      } else {
        setError(getPersianErrorMessage(err, translateAuthError(err)));
        // Reset captcha on other errors
        if (showCaptcha && turnstileRef.current) {
          turnstileRef.current.reset();
          setCaptchaToken('');
        }
      }
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <h2>ورود به حساب کاربری</h2>

        {successMessage && (
          <div
            className="success-message"
            style={{
              background: '#d1fae5',
              color: '#065f46',
              padding: '0.75rem',
              borderRadius: '8px',
              marginBottom: '1rem',
            }}
          >
            {successMessage}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleLogin} noValidate>
          <div className="form-group">
            <label htmlFor="phoneNumber">شماره موبایل</label>

            <input
              id="phoneNumber"
              type="tel"
              inputMode="numeric"
              placeholder="شماره تماس بدون صفر (مثلا ۹۱۲۳۴۵۶۷۸۹)"
              value={phoneNumber}
              onChange={handlePhoneChange}
              onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
              aria-invalid={!!phoneError}
              aria-describedby={phoneError ? 'phoneNumber-error' : undefined}
              maxLength={10}
              required
            />
            {phoneError && (
              <span
                id="phoneNumber-error"
                className="field-error"
                style={{ color: '#b91c1c', fontSize: '0.85rem' }}
              >
                {phoneError}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">رمز عبور</label>

            <div className="password-field">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="رمز عبور را وارد کنید"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? 'password-error' : undefined}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((show) => !show)}
                aria-label={showPassword ? 'مخفی کردن رمز عبور' : 'نمایش رمز عبور'}
                tabIndex={-1}
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </div>
            {passwordError && (
              <span
                id="password-error"
                className="field-error"
                style={{ color: '#b91c1c', fontSize: '0.85rem' }}
              >
                {passwordError}
              </span>
            )}
          </div>

          {showCaptcha && turnstileSiteKey && (
            <div className="form-group">
              <TurnstileWidget
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                action="login"
                onToken={(token) => {
                  setCaptchaToken(token);
                  setError('');
                }}
                onExpire={() => setCaptchaToken('')}
                onError={() => setCaptchaToken('')}
              />
            </div>
          )}

          <p className="forgot-password-link">
            <Link to="/forgot-password">فراموشی رمز عبور؟</Link>
          </p>

          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? 'درحال ورود...' : 'ورود'}
          </button>
        </form>

        <p className="signup-link">
          حساب کاربری ندارید؟{' '}
          <Link to="/signup">ثبت نام کنید</Link>
        </p>
      </div>
    </div>
  );
}
